import { Fragment, FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Knob } from "primereact/knob";
import { ProgressSpinner } from "primereact/progressspinner";

import { faRotate } from "@fortawesome/pro-regular-svg-icons";

import { useFormatCurrencyHook } from "../../../../../utils/hooks/formatCurrencyHook";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useCurrentUserService } from "../../../../services/ServiceHooks";
import { Device } from "../../../../stores";
import { useStore } from "../../../../stores/StoreContext";
import { ActionButton } from "../../Buttons";
import { TooltipWrapper } from "../TooltipWrapper/TooltipWrapper";

import "./CurrentUserBudgetInfo.scss";

const budgetWarningColor = "var(--customer-warning-color, #ff9800)";
const budgetDangerColor = "var(--customer-error-color, #f44336)";
const budgetDefaultColor = "var(--customer-brand-color-background, #5531d5)";
const budgetRangeColor = "var(--customer-button-gradient-background-hover, #e2def0ff)";

const CurrentUserBudgetInfoRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { budgetStore, breakpointStore } = useStore();
  const currentUserService = useCurrentUserService();
  const [isBudgetInfoLoading, setIsBudgetInfoLoading] = useState(false);

  const formatCurrency = useFormatCurrencyHook();

  // Currently hardcoding currency to EUR until multi-currency support is implemented
  const currency = "EUR";

  const refreshBudgetInfo = async () => {
    setIsBudgetInfoLoading(true);
    await currentUserService.ensureHasBudget();
    setIsBudgetInfoLoading(false);
  };

  const usageColor = useCallback(() => {
    const usage = budgetStore.budgetInfoValue.currentUsage ?? 0;
    const limit = budgetStore.budgetInfoValue.budgetLimit ?? 1;
    const usagePercentage = (usage / limit) * 100;
    if (usagePercentage >= 90) {
      return budgetDangerColor;
    } else if (usagePercentage >= 75) {
      return budgetWarningColor;
    } else {
      return budgetDefaultColor;
    }
  }, [budgetStore.budgetInfoValue]);

  const knobStrokeWidth = useCallback(() => {
    return breakpointStore.isSmallerThan(Device.Desktop) ? 18 : 20;
  }, [breakpointStore.device]);

  if (!budgetStore.budgetInfoValue.currentUsage && !budgetStore.budgetInfoValue.budgetLimit) {
    return (
      <div className="current-user-budget-info" id="currentUsageKnobError">
        <div className="error">
          <TooltipWrapper
            targetRef="#currentUsageKnobError"
            position="bottom"
            content={t(translationKeys.budget.failedToLoadBudgetInfo)}
          />
          {t(translationKeys.budget.failedToLoadBudgetInfo)}
        </div>
      </div>
    );
  }

  return (
    <div className="current-user-budget-info" id="currentUsageKnob">
      <TooltipWrapper
        targetRef="#currentUsageKnob"
        position="bottom"
        content={t(translationKeys.budget.currentBudgetInfoTooltip, {
          currentUsage: formatCurrency(budgetStore.budgetInfoValue.currentUsage ?? 0, currency),
          budgetLimit: formatCurrency(budgetStore.budgetInfoValue.budgetLimit ?? 0, currency),
        })}
      />

      <div className="label">{formatCurrency(budgetStore.budgetInfoValue.currentUsage ?? 0, currency)}</div>

      <div className="budget-knob-container">
        {!isBudgetInfoLoading && (
          <Fragment>
            <Knob
              className="budget-knob"
              step={budgetStore.budgetInfoValue.currentUsage ?? 0}
              max={budgetStore.budgetInfoValue.budgetLimit ?? 1}
              size={1.25 * 16} // "lg" size of the icon (1.25rem in pixels)
              value={budgetStore.budgetInfoValue.currentUsage ?? 0}
              valueTemplate=""
              strokeWidth={knobStrokeWidth()}
              rangeColor={budgetRangeColor}
              valueColor={usageColor()}
            />

            <ActionButton
              id="refreshBudgetInfoButton"
              onClick={refreshBudgetInfo}
              icon={faRotate}
              removeSpaces
              customClasses="budget-refresh-icon"
            />
          </Fragment>
        )}

        {!!isBudgetInfoLoading && <ProgressSpinner className="small-inline-spinner" color={budgetDefaultColor} />}
      </div>
    </div>
  );
};

export const CurrentUserBudgetInfo = observer(CurrentUserBudgetInfoRaw);
